import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Wine, Clock, Phone, MapPin, ArrowLeft, Music, 
  Sparkles, Star, MessageCircle 
} from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import { BAR_LOUNGE, PROPERTY_IMAGES } from '../utils/constants';
import { BAR_MENU } from '../utils/cinematicData';

const BarLoungePage = () => {
  const [activeCategory, setActiveCategory] = useState('cocktails');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const whatsappLink = `https://wa.me/917554229796?text=Hi, I'd like to reserve a table at 101 Bar & Lounge.`;

  const menuCategories = [
    { id: 'cocktails', name: 'Signature Cocktails', icon: Wine },
    { id: 'spirits', name: 'Premium Spirits', icon: Sparkles },
    { id: 'appetizers', name: 'Bar Bites', icon: Star }
  ];

  return (
    <div className="bg-navy-900 min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={BAR_LOUNGE.images?.hero || PROPERTY_IMAGES.jalsaRetreat.dining}
            alt="101 Bar & Lounge"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/70 to-navy-900/50" />
        </div>

        {/* Back Button */}
        <Link
          to="/"
          className="absolute top-24 left-6 z-20 flex items-center space-x-2 text-white hover:text-gold-400 transition-colors duration-300"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-light">Back to Home</span>
        </Link>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 z-20 p-8 md:p-16">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <div className="flex items-center space-x-4 mb-6">
                <Wine className="w-8 h-8 text-gold-400" />
                <span className="text-gold-400 tracking-widest uppercase text-sm">Exclusive Lounge</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-serif text-white mb-6">
                {BAR_LOUNGE.name}
              </h1>
              
              <p className="text-xl md:text-2xl text-gold-400 font-light italic mb-8">
                "{BAR_LOUNGE.tagline}"
              </p>

              <div className="flex flex-wrap items-center gap-6 text-gray-300">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-5 h-5 text-gold-400" />
                  <span>{BAR_LOUNGE.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-gold-400" />
                  <span>{BAR_LOUNGE.timings}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Decorative Gold Line */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent" />
      </section>

      {/* About Section */}
      <section className="py-20 bg-gradient-to-b from-navy-900 to-navy-800">
        <div className="container mx-auto px-4">
          <ScrollReveal direction="up">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-xl text-gray-300 leading-relaxed mb-12">
                {BAR_LOUNGE.description}
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {BAR_LOUNGE.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="bg-white/5 backdrop-blur-sm border border-gold-400/20 rounded-lg p-6 hover:border-gold-400/60 transition-all duration-300"
                  >
                    <span className="text-gold-400">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Digital Menu Section */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <ScrollReveal direction="up">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-6">
                <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-400" />
                <Music className="mx-4 w-6 h-6 text-gold-400" />
                <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-400" />
              </div>
              <h2 className="text-4xl md:text-5xl font-serif text-white mb-4">
                Our Menu
              </h2>
              <p className="text-gray-400">Curated selection of premium beverages and bar bites</p>
            </div>
          </ScrollReveal>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {menuCategories.map((category) => (
              <motion.button
                key={category.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-gold-400 text-navy-900'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <category.icon className="w-5 h-5" />
                <span>{category.name}</span>
              </motion.button>
            ))}
          </div>

          {/* Menu Items */}
          <ScrollReveal direction="up">
            <div className="max-w-4xl mx-auto">
              {activeCategory === 'cocktails' && (
                <div className="grid md:grid-cols-2 gap-6">
                  {BAR_MENU.cocktails.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      className="bg-white/5 backdrop-blur-sm border border-gold-400/20 rounded-lg p-6 hover:border-gold-400/60 transition-all duration-300"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-xl font-serif text-gold-400">{item.name}</h3>
                        <span className="text-white font-bold">{item.price}</span>
                      </div>
                      <p className="text-gray-400 text-sm">{item.description}</p>
                    </motion.div>
                  ))}
                </div>
              )}

              {activeCategory === 'spirits' && (
                <div className="grid md:grid-cols-2 gap-6">
                  {BAR_MENU.spirits.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      className="bg-white/5 backdrop-blur-sm border border-gold-400/20 rounded-lg p-6 hover:border-gold-400/60 transition-all duration-300"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-xl font-serif text-gold-400">{item.name}</h3>
                        <span className="text-white font-bold">{item.price}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {item.brands.map((brand, idx) => (
                          <span key={idx} className="text-xs bg-white/10 text-gray-300 px-2 py-1 rounded">
                            {brand}
                          </span>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {activeCategory === 'appetizers' && (
                <div className="grid md:grid-cols-2 gap-6">
                  {BAR_MENU.appetizers.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      className="bg-white/5 backdrop-blur-sm border border-gold-400/20 rounded-lg p-6 hover:border-gold-400/60 transition-all duration-300"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-xl font-serif text-gold-400">{item.name}</h3>
                        <span className="text-white font-bold">{item.price}</span>
                      </div>
                      <p className="text-gray-400 text-sm">{item.description}</p>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Ambiance Gallery */}
      <section className="py-20 bg-navy-900">
        <div className="container mx-auto px-4">
          <ScrollReveal direction="up">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif text-white mb-4">The Experience</h2>
              <p className="text-gray-400">Where sophistication meets celebration</p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              { image: BAR_LOUNGE.images?.hero, title: 'Premium Bar' },
              { image: BAR_LOUNGE.images?.ambiance, title: 'Lounge Ambiance' },
              { image: BAR_LOUNGE.images?.cocktail, title: 'Expert Mixology' }
            ].map((item, index) => (
              <ScrollReveal key={index} direction="up" delay={index * 0.1}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative h-64 rounded-lg overflow-hidden group"
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="text-lg font-serif text-white">{item.title}</span>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Reservation CTA */}
      <section className="py-20 bg-gradient-to-b from-navy-900 to-navy-800">
        <div className="container mx-auto px-4">
          <ScrollReveal direction="up">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl font-serif text-white mb-6">
                Reserve Your Table
              </h2>
              <p className="text-gray-300 text-lg mb-10">
                Experience an unforgettable evening at 101 Bar & Lounge. 
                Reserve your spot and let us take care of the rest.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href={`tel:${BAR_LOUNGE.contact}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center justify-center space-x-2 px-8 py-4 bg-gold-400 text-navy-900 rounded-lg font-bold text-lg hover:bg-gold-500 transition-colors duration-300"
                >
                  <Phone className="w-5 h-5" />
                  <span>Call to Reserve</span>
                </motion.a>
                
                <motion.a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center justify-center space-x-2 px-8 py-4 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 transition-colors duration-300"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp Us</span>
                </motion.a>
              </div>

              {/* Info */}
              <div className="flex flex-wrap justify-center gap-8 mt-12 text-gray-400">
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-gold-400" />
                  <span>{BAR_LOUNGE.timings}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-5 h-5 text-gold-400" />
                  <span>{BAR_LOUNGE.contact}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-5 h-5 text-gold-400" />
                  <span>{BAR_LOUNGE.location}</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default BarLoungePage;
